import Link from "@/components/ui/Link";
import {
  getBlogPostUrl,
  getBlogCategoryUrl,
} from "@/lib/blog";
import Pagination from "@/components/Blog/BlogCategoryListSection/Pagination";
import { getReadableImageUrl } from "@/lib/directus";
import Image from "next/image";
import { htmlToPlainText, createExcerpt } from "../utils";
import { useState, useEffect } from "react";
import BlogTabs from "../BlogTabs/BlogTabs";
import BlogOrder from "../BlogOrder/BlogOrder";
import { useRouter } from "next/router";
import AuthorDisplay from "../AuthorDisplay";

export interface BlogCategoryListSectionData {
  posts: {
    id: string;
    title: string;
    slug: string;
    content: string;
    author: {
      slug: string;
      title: string;
    };
    categories: {
      blog_categories_id: {
        slug: string;
        title: string;
      };
    }[];

    published_at: string;
    featured_image: {
      id: string;
      filename_download: string;
    };
  }[];
  total: number;
  categorySlug: string;
  totalPages: number;
  currentPage: number;
  left_column_data: any;
  categories: {
    id: string;
    slug: string;
    title: string;
  }[];
  archives: {
    date: string;
    count: number;
  }[];
}

interface BlogCategoryListSectionProps {
  data: BlogCategoryListSectionData;
}

export default function BlogCategoryListSection({
  data,
}: BlogCategoryListSectionProps) {
  const [mounted, setMounted] = useState(false);
  const [displayedPosts, setDisplayedPosts] = useState(data.posts);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Sort posts based on URL sort parameter
    const sortOrder = router.query.sort as string || 'newest';
    const sortedPosts = [...data.posts].sort((a, b) => {
      const dateA = new Date(a.published_at).getTime();
      const dateB = new Date(b.published_at).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    setDisplayedPosts(sortedPosts);
  }, [router.query.sort, data.posts]);

  const formatDate = (date: string) => {
    try {
      return new Date(date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    } catch (e) {
      return date;
    }
  };

  // Transform categories data to match BlogTabs expected format
  const transformedCategories = data.categories?.map(category => ({
    id: category.id,
    slug: category.slug,
    title: category.title
  })) || [];

  return (
		<>
			{!data.posts.length && (
				<div className='text-center py-16'>
					<p className='text-neutral-600'>No posts found for this category.</p>
				</div>
			)}
			<section className='BlogCategoryList py-16'>
				<div className='container mx-auto max-w-[1400px] px-4'>
					<div className='flex flex-col'>
						<div className='BlogNav mb-16 max-md:justify-center max-md:flex flex-wrap'>
							<div className='flex justify-between items-center'>
								<BlogTabs
									data={{
										categories: data.categories,
										archives: data.archives,
									}}
									activeCategorySlug={data.categorySlug}
								/>
							</div>
							<div className='flex justify-end items-center mt-8'>
								<BlogOrder />
							</div>
						</div>

						<div className='order-1 md:order-2'>
							<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10'>
								{displayedPosts.map(post => (
									<article
										key={post.id}
										className='overflow-hidden flex flex-col h-full'
									>
										<Link href={getBlogPostUrl(post.slug)} className='block'>
											<div className='relative aspect-[16/10] w-full'>
												<Image
													src={
														post.featured_image
															? getReadableImageUrl(
																	post.featured_image.id,
																	post.featured_image.filename_download
															  )
															: '/images/blog-placeh2.webp'
													}
													alt={htmlToPlainText(post.title) || ''}
													fill
													className='object-cover'
													sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'
													quality={95}
													loading={'lazy'}
												/>
											</div>
										</Link>

										<div className='py-8 flex flex-col flex-grow'>
											<h2 className='text-3xl md:text-4xl mb-6'>
												<Link
													href={getBlogPostUrl(post.slug)}
													className='hover:text-brown'
												>
													{htmlToPlainText(post.title)}
												</Link>
											</h2>

										<div className='prose prose-neutral max-w-none mb-6 flex-grow'>
											<p className='text-base line-clamp-[6]'>
												{createExcerpt(post.content, 400)}
											</p>
										</div>

											<div className='grid'>
												<div>
													<AuthorDisplay author={post.author} />
												</div>
												{/* <div className='flex gap-2'>
													<span>
														<time dateTime={post.published_at}>
															{mounted ? formatDate(post.published_at) : ''}
														</time>
													</span>
													<span>•</span>
													<span>5 min read</span>
												</div> */}
											</div>
										</div>
									</article>
								))}
							</div>

							<Pagination
								currentPage={data.currentPage || 1}
								totalPages={data.totalPages}
								baseUrl={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG}/${data.categorySlug}`}
							/>
						</div>
					</div>
				</div>
			</section>
		</>
	)
}
